/**
 * LandingPage.jsx
 * Executive-level status overview across ALL assets.
 *
 * Shows ONLY:
 *   • Production Metrics  (EntityType === 'Metric', Category does NOT contain 'HSE')
 *   • Projects            (EntityType === 'Project')
 *
 * Traffic light logic (per entity):
 *   RED    – own status is bad (target missed / Not Met / Delayed / Blocked)
 *            OR any unmet dependency rule with Priority < CRITICAL_THRESHOLD (50)
 *   AMBER  – own status is At Risk, OR unmet rules that are all minor (Priority ≥ 50)
 *   GREEN  – own status is good AND all dependency rules are met
 *   GREY   – no signal data
 *
 * Priority convention when creating rules:
 *   Priority 1–49  → critical  →  triggers RED when unmet
 *   Priority 50–100→ minor     →  triggers AMBER when unmet
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import { DEFAULT_SETTINGS, isNotificationDue } from '../services/notificationService';
import sharePointService from '../services/sharePointService';
import './LandingPage.css';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CRITICAL_THRESHOLD = 50;

const GOOD_STATUSES    = new Set(['Met', 'On Track', 'On-Track', 'Completed', 'Active']);
const AT_RISK_STATUSES = new Set(['At Risk', 'At-Risk']);

const TYPE_CONFIG = {
  Metric:  { color: '#2563EB', bg: '#EFF6FF', icon: '📈', label: 'Production Metric' },
  Project: { color: '#7C3AED', bg: '#F5F3FF', icon: '🏗️', label: 'Project' },
};

const STATUS_LABELS = {
  green: 'On Target',
  amber: 'At Risk',
  red:   'Critical',
  grey:  'No Data',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

/** Returns a plain-string status for an entity, handling both string and object deadlineStatus. */
function getEntityOwnStatus(entity, latestSignals) {
  if (entity.ytdStatus) return entity.ytdStatus;

  // projectTimelineEngine sets deadlineStatus as an object
  // { status, statusColor, message, isOverdue, daysRemaining, ... }
  if (entity.deadlineStatus) {
    const ds = entity.deadlineStatus;
    if (typeof ds === 'string' && ds !== 'none' && ds !== 'no-deadline') return ds;
    if (typeof ds === 'object' && ds.status && ds.status !== 'none' && ds.status !== 'no-deadline') {
      return ds.status;
    }
  }

  const signal = latestSignals?.get(entity.Id);
  return signal?.Status || null;
}

/**
 * True if entity should appear on this overview.
 * Production Metrics (non-HSE) and Projects only.
 */
function isProductionOrProject(entity) {
  if (entity.EntityType === 'Project') return true;
  if (entity.EntityType === 'Metric') {
    const cat = (entity.Category || '').toLowerCase();
    return !cat.includes('hse');
  }
  return false;
}

/**
 * Compute traffic light for one entity.
 * Returns { color, rulesMet, totalRules, unmetRules, ownStatus, targetMissed }
 */
function computeTrafficLight(entity, ruleEvaluations, latestSignals) {
  const entityRules = (ruleEvaluations || []).filter(
    (r) => Number(r.targetEntityId) === Number(entity.Id),
  );

  const metRules   = entityRules.filter((r) => r.conditionMet);
  const unmetRules = entityRules.filter((r) => !r.conditionMet);

  const ownStatus   = getEntityOwnStatus(entity, latestSignals);
  const ownIsGood   = GOOD_STATUSES.has(ownStatus);
  const ownIsAtRisk = AT_RISK_STATUSES.has(ownStatus);
  const ownIsBad    = ownStatus && !ownIsGood && !ownIsAtRisk; // Not Met / Delayed / Blocked

  const base = { rulesMet: metRules.length, totalRules: entityRules.length, unmetRules, ownStatus };

  // ── Own performance is bad (e.g. production target missed) ── always RED
  if (ownIsBad) {
    return { ...base, color: 'red', targetMissed: entity.EntityType === 'Metric' };
  }

  // ── No dependency rules ──────────────────────────────────────────────────
  if (entityRules.length === 0) {
    if (!ownStatus)   return { ...base, color: 'grey',  targetMissed: false };
    if (ownIsGood)    return { ...base, color: 'green', targetMissed: false };
    return              { ...base, color: 'amber', targetMissed: false }; // At Risk
  }

  // ── Has dependency rules ─────────────────────────────────────────────────
  if (unmetRules.length === 0) {
    // All rules met — own status at risk still warrants amber
    return { ...base, color: ownIsAtRisk ? 'amber' : 'green', targetMissed: false };
  }

  const hasCritical = unmetRules.some((r) => (r.priority ?? 10) < CRITICAL_THRESHOLD);
  return { ...base, color: hasCritical ? 'red' : 'amber', targetMissed: false };
}

// ─── Condition expression formatter ────────────────────────────────────────────

function formatConditionHuman(expression, signal) {
  if (!expression) return null;
  const expr = expression.trim();

  // NumericValue comparisons  e.g.  NumericValue >= 0.5  or  NumericValue >= 5000
  const numericMatch = expr.match(/NumericValue\s*(>=|<=|>|<|==|!=)\s*([0-9.]+)/i);
  if (numericMatch) {
    const [, op, rawVal] = numericMatch;
    const threshold = parseFloat(rawVal);
    const isScore = threshold >= 0 && threshold <= 1;

    const opLabel = { '>=': 'at least', '<=': 'at most', '>': 'above', '<': 'below', '==': 'exactly', '!=': 'not' };
    const fmtThreshold = isScore
      ? `${(threshold * 100).toFixed(0)}%`
      : threshold.toLocaleString();

    const currentNum = signal?.NumericValue ?? null;
    const currentDisplay = currentNum !== null
      ? isScore ? `${(currentNum * 100).toFixed(0)}%` : Number(currentNum).toLocaleString()
      : null;

    const label = isScore ? 'Score' : 'Value';
    return `${label} must be ${opLabel[op] || op} ${fmtThreshold}${currentDisplay !== null ? ` (current: ${currentDisplay})` : ''}`;
  }

  // Status comparisons  e.g.  Status == "Met"
  const statusMatch = expr.match(/Status\s*(==|!=)\s*["']([^"']+)["']/i);
  if (statusMatch) {
    const [, op, val] = statusMatch;
    const opLabel = op === '==' ? 'must be' : 'must not be';
    const current = signal?.Status || null;
    return `Status ${opLabel} "${val}"${current ? ` (current: "${current}")` : ''}`;
  }

  // BooleanValue comparisons  e.g.  BooleanValue == true
  const boolMatch = expr.match(/BooleanValue\s*(==|!=)\s*(true|false)/i);
  if (boolMatch) {
    const [, op, val] = boolMatch;
    const needsTrue = (op === '==' && val === 'true') || (op === '!=' && val === 'false');
    return needsTrue ? 'Must be active / confirmed' : 'Must be inactive / not confirmed';
  }

  // Fallback — return the raw expression unchanged
  return expr;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function StatusDot({ color }) {
  return (
    <span
      className={`status-dot status-dot--${color}`}
      aria-label={STATUS_LABELS[color]}
      title={STATUS_LABELS[color]}
    />
  );
}

function RuleRow({ rule, latestSignals, entities }) {
  const srcType  = rule.sourceEntityType || 'Entity';
  const typeConf = TYPE_CONFIG[srcType] || {};
  // latestSignals keys are entity.Id (string from Graph API item.id).
  // rule.sourceEntityId is an integer from SharePoint fields — try all type variants.
  const _srcId = rule.sourceEntityId;
  const signal = latestSignals?.get(_srcId)
    ?? latestSignals?.get(String(_srcId))
    ?? latestSignals?.get(Number(_srcId));
  const signalStatus = signal?.Status || '—';
  const priority = rule.priority ?? 10;
  const isCritical = priority < CRITICAL_THRESHOLD;

  console.log('Rule', rule);
  console.log(signal)
  // Find source entity to show its owner
  const sourceEntity = entities?.find((e) => Number(e.Id) === Number(rule.sourceEntityId));
  const owner = sourceEntity?.Owner || null;

  const statusClass = GOOD_STATUSES.has(signalStatus)
    ? 'text-green'
    : AT_RISK_STATUSES.has(signalStatus)
    ? 'text-amber'
    : 'text-red';

  return (
    <div className={`rule-row rule-row--${isCritical ? 'critical' : 'minor'}`}>
      <div className="rule-row__header">
        <span
          className="rule-row__type-badge"
          style={{ background: typeConf.bg || '#F3F4F6', color: typeConf.color || '#374151' }}
        >
          {typeConf.icon || '🔗'} {srcType}
        </span>
        <span className="rule-row__name">{rule.sourceEntity || 'Unknown'}</span>
        <span className={`priority-badge priority-badge--${isCritical ? 'critical' : 'minor'}`}>
          P{priority}
        </span>
      </div>

      <div className="rule-row__meta">
        <span className="rule-row__status">
          Status: <strong className={statusClass}>{signalStatus}</strong>
        </span>
        {rule.ruleType && <span className="rule-row__sep">·</span>}
        {rule.ruleType && (
          <span className="rule-row__type">{rule.ruleType}</span>
        )}
      </div>
      {owner && (
        <div className="rule-row__owner">
          Owner: <strong>{owner}</strong>
        </div>
      )}

      {rule.conditionExpression && (
        <div className="rule-row__condition">
          Condition: <span className="condition-human">{formatConditionHuman(rule.conditionExpression, signal)} — <em>not met</em></span>
        </div>
      )}
      {signal?.Reason && (
        <div className="rule-row__reason">
          💬 <strong>Reason:</strong> {signal.Reason}
        </div>
      )}
      {rule.ruleTitle && (
        <div className="rule-row__title">{rule.ruleTitle}</div>
      )}
    </div>
  );
}

function FailingDepsModal({ entity, trafficLight, latestSignals, entities, assetName, onClose }) {
  const { unmetRules, color, rulesMet, totalRules, ownStatus, targetMissed } = trafficLight;
  const typeConf  = TYPE_CONFIG[entity.EntityType] || {};
  const _eid = entity.Id;
  const entitySignal = latestSignals?.get(_eid) ?? latestSignals?.get(String(_eid)) ?? latestSignals?.get(Number(_eid));
  const entityReason = entitySignal?.Reason || null;

  const _ds = entity.deadlineStatus;
  const deadlineMessage = _ds && typeof _ds === 'object'
    && _ds.message && _ds.status !== 'none' && _ds.status !== 'no-deadline'
    ? _ds.message
    : null;

  const csfUnmet   = unmetRules.filter((r) => r.sourceEntityType === 'CSF');
  const otherUnmet = unmetRules.filter((r) => r.sourceEntityType !== 'CSF');

  // YTD figures for metric
  const hasYTD = entity.EntityType === 'Metric' && entity.TargetValue;
  const ytdVal = entity.ytdAverage ?? null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div className="modal-header__left">
            <div className="modal-header__badges">
              <span
                className="modal-type-badge"
                style={{ background: typeConf.bg, color: typeConf.color }}
              >
                {typeConf.icon} {typeConf.label || entity.EntityType}
              </span>
              {assetName && (
                <span className="modal-asset-badge">{assetName}</span>
              )}
            </div>
            <h2 className="modal-title">{entity.Title}</h2>
            {entity.Category && (
              <span className="modal-category">{entity.Category}</span>
            )}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Status banner */}
        <div className={`modal-status-banner modal-status-banner--${color}`}>
          <StatusDot color={color} />
          <div className="modal-status-text">
            <strong>{STATUS_LABELS[color]}</strong>
            {totalRules > 0 && (
              <span>&nbsp;—&nbsp;{rulesMet}/{totalRules} dependency rules met</span>
            )}
            {totalRules === 0 && ownStatus && (
              <span>&nbsp;—&nbsp;Status: {ownStatus}</span>
            )}
          </div>
        </div>

        <div className="modal-body">

          {/* Entity's own reason for non-performance */}
          {entityReason && (
            <div className="modal-reason-banner">
              💬 <strong>Reason for non-performance:</strong> {entityReason}
            </div>
          )}

          {/* Deadline proximity reason */}
          {deadlineMessage && (
            <div className="modal-reason-banner">
              📅 <strong>Deadline:</strong> {deadlineMessage}
            </div>
          )}

          {/* Target missed banner (production metrics) */}
          {targetMissed && hasYTD && (
            <div className="modal-target-missed">
              <div className="target-missed__icon">🎯</div>
              <div className="target-missed__text">
                <strong>Production Target Missed</strong>
                <div className="target-missed__figures">
                  <span>
                    Target:&nbsp;
                    <strong>{Number(entity.TargetValue).toLocaleString()}&nbsp;{entity.TargetUnit || ''}</strong>
                  </span>
                  {ytdVal !== null && (
                    <span>
                      YTD Average:&nbsp;
                      <strong className="text-red">
                        {Number(ytdVal).toLocaleString()}&nbsp;{entity.TargetUnit || ''}
                      </strong>
                    </span>
                  )}
                  {ytdVal !== null && entity.TargetValue > 0 && (
                    <span>
                      Achievement:&nbsp;
                      <strong className="text-red">
                        {((ytdVal / entity.TargetValue) * 100).toFixed(1)}%
                      </strong>
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Falling CSFs */}
          {csfUnmet.length > 0 && (
            <section className="modal-section">
              <h3 className="modal-section__title modal-section__title--csf">
                ⭐ Non-Performing Critical Success Factors
                <span className="section-count">{csfUnmet.length}</span>
              </h3>
              <p className="modal-section__subtitle">
                These CSFs have unmet conditions directly impacting this entity.
              </p>
              <div className="rules-list">
                {csfUnmet
                  .sort((a, b) => (a.priority ?? 10) - (b.priority ?? 10))
                  .map((rule, idx) => (
                    <RuleRow
                      key={rule.ruleId || idx}
                      rule={rule}
                      latestSignals={latestSignals}
                      entities={entities}
                    />
                  ))}
              </div>
            </section>
          )}

          {/* Other unmet dependencies */}
          {otherUnmet.length > 0 && (
            <section className="modal-section">
              <h3 className="modal-section__title">
                🔗 Other Failing Dependencies
                <span className="section-count">{otherUnmet.length}</span>
              </h3>
              <div className="rules-list">
                {otherUnmet
                  .sort((a, b) => (a.priority ?? 10) - (b.priority ?? 10))
                  .map((rule, idx) => (
                    <RuleRow
                      key={rule.ruleId || idx}
                      rule={rule}
                      latestSignals={latestSignals}
                      entities={entities}
                    />
                  ))}
              </div>
            </section>
          )}

          {/* Green / no data */}
          {unmetRules.length === 0 && !targetMissed && !deadlineMessage && (
            <div className="modal-empty">
              <p>
                {ownStatus
                  ? `This entity's status is derived from its signal data: ${ownStatus}.`
                  : 'No failing dependencies detected.'}
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-close-modal" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function EntityCard({ entity, trafficLight, onClick, floatDelay }) {
  const { color, rulesMet, totalRules, ownStatus, targetMissed } = trafficLight;
  const typeConf = TYPE_CONFIG[entity.EntityType] || {};
  const isClickable = color !== 'green' && color !== 'grey';

  return (
    <div
      className={`entity-card entity-card--${color} ${isClickable ? 'entity-card--clickable' : ''}`}
      style={{ '--float-delay': `${floatDelay}s` }}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      {/* Top row: type + status */}
      <div className="ec-top">
        <span className="ec-type" style={{ background: typeConf.bg, color: typeConf.color }}>
          {typeConf.icon} {entity.EntityType}
        </span>
        <div className="ec-status">
          <StatusDot color={color} />
          <span className={`ec-status__label ec-status__label--${color}`}>
            {targetMissed ? 'Missed' : STATUS_LABELS[color]}
          </span>
        </div>
      </div>

      {/* Entity name */}
      <h3 className="ec-name">{entity.Title}</h3>

      {/* Category */}
      {entity.Category && <span className="ec-cat">{entity.Category}</span>}

      {/* Owner — only rendered if present */}
      {entity.Owner && (
        <div className="ec-owner">
          <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
            <path d="M8 8C9.657 8 11 6.657 11 5C11 3.343 9.657 2 8 2C6.343 2 5 3.343 5 5C5 6.657 6.343 8 8 8Z" fill="currentColor"/>
            <path d="M3 14C3 11.791 4.791 10 7 10H9C11.209 10 13 11.791 13 14H3Z" fill="currentColor"/>
          </svg>
          {entity.Owner}
        </div>
      )}

      {/* Metric figures */}
      {entity.TargetValue && (
        <div className="ec-figs">
          <div className="ec-fig">
            <span className="ec-fig__label">Target</span>
            <span className="ec-fig__value">
              {Number(entity.TargetValue).toLocaleString()}
              {entity.TargetUnit && <em className="ec-fig__unit">{entity.TargetUnit}</em>}
            </span>
          </div>
          {entity.ytdAverage != null && (
            <div className="ec-fig">
              <span className="ec-fig__label">YTD</span>
              <span className={`ec-fig__value ${targetMissed ? 'text-red' : ''}`}>
                {Number(entity.ytdAverage).toLocaleString()}
                {entity.TargetUnit && <em className="ec-fig__unit">{entity.TargetUnit}</em>}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Plain status when no target value */}
      {!entity.TargetValue && ownStatus && (
        <div className="ec-own-status">{ownStatus}</div>
      )}

      {/* Rules mini-bar */}
      {totalRules > 0 && (
        <div className="ec-rules">
          <div className="ec-rules__track">
            <div
              className={`ec-rules__fill ec-rules__fill--${color}`}
              style={{ width: `${(rulesMet / totalRules) * 100}%` }}
            />
          </div>
          <span className="ec-rules__label">{rulesMet}/{totalRules}</span>
        </div>
      )}
    </div>
  );
}

// ─── Asset Section ──────────────────────────────────────────────────────────────

function AssetSection({ assetData, colorFilter, onCardClick }) {
  const { asset, entities, ruleEvaluations, latestSignals } = assetData;

  const relevant = entities.filter(isProductionOrProject);

  const withStatus = relevant.map((entity) => ({
    entity,
    trafficLight: computeTrafficLight(entity, ruleEvaluations, latestSignals),
  }));

  const nonGreen = withStatus.filter(({ trafficLight }) => trafficLight.color !== 'green');
  const visible = colorFilter === 'All'
    ? nonGreen
    : nonGreen.filter(({ trafficLight }) => trafficLight.color === colorFilter);

  if (visible.length === 0) return null;

  const redCount   = withStatus.filter(({ tl }) => tl?.color === 'red').length   || withStatus.filter(({ trafficLight }) => trafficLight.color === 'red').length;
  const amberCount = withStatus.filter(({ trafficLight }) => trafficLight.color === 'amber').length;
  const greenCount = withStatus.filter(({ trafficLight }) => trafficLight.color === 'green').length;

  return (
    <section className="asset-section">
      <div className="asset-section__heading">
        <div className="asset-section__title-row">
          <h3 className="asset-section__name">{asset.Title}</h3>
          {asset.AssetCode && (
            <span className="asset-section__code">{asset.AssetCode}</span>
          )}
        </div>
        <div className="asset-section__pills">
          {greenCount > 0 && <span className="asset-pill asset-pill--green"><span className="asset-pill-dot" />{greenCount}</span>}
          {amberCount > 0 && <span className="asset-pill asset-pill--amber"><span className="asset-pill-dot" />{amberCount}</span>}
          {redCount > 0   && <span className="asset-pill asset-pill--red"><span className="asset-pill-dot" />{redCount}</span>}
        </div>
      </div>

      <div className="entity-grid">
        {visible.map(({ entity, trafficLight }, idx) => (
          <EntityCard
            key={entity.Id}
            entity={entity}
            trafficLight={trafficLight}
            floatDelay={idx * 0.35}
            onClick={() => onCardClick(entity, trafficLight, assetData)}
          />
        ))}
      </div>
    </section>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const LandingPage = () => {
  const navigate = useNavigate();

  const {
    allAssetsOverview,
    isFetchingOverview,
    fetchAllAssetsOverview,
    assets,
    userRole,
  } = useStore();

  const [colorFilter, setColorFilter] = useState('All');
  const [showNotifBanner, setShowNotifBanner] = useState(false);

  // State for the popup: { entity, trafficLight, assetData }
  const [modal, setModal] = useState(null);

  // Load all-assets data when component mounts or when assets list changes
  useEffect(() => {
    if (assets.length > 0) {
      fetchAllAssetsOverview();
    }
  }, [assets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-prompt: show banner to admins if today is a schedule day and notifications haven't been sent
  useEffect(() => {
    if (userRole !== 'Admin') return;
    (async () => {
      try {
        const sp = await sharePointService.getNotificationSettings();
        const settings = sp ? { ...DEFAULT_SETTINGS, ...sp } : DEFAULT_SETTINGS;
        if (!settings.enableAutoPrompt) return;
        const lastSent = sp?.lastSent || {};
        const csfDue = isNotificationDue('csf', settings.scheduleDays, lastSent);
        const dlDue  = isNotificationDue('deadline', settings.scheduleDays, lastSent);
        if (csfDue || dlDue) setShowNotifBanner(true);
      } catch {
        // silently ignore — banner is non-critical
      }
    })();
  }, [userRole]);

  // ── Global summary counts across ALL assets ─────────────────────────────
  const summary = useMemo(() => {
    const counts = { green: 0, amber: 0, red: 0, grey: 0 };
    allAssetsOverview.forEach(({ entities, ruleEvaluations, latestSignals }) => {
      entities.filter(isProductionOrProject).forEach((entity) => {
        const tl = computeTrafficLight(entity, ruleEvaluations, latestSignals);
        counts[tl.color]++;
      });
    });
    return counts;
  }, [allAssetsOverview]);

  const totalEntities = summary.amber + summary.red + summary.grey;

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleCardClick = (entity, trafficLight, assetData) => {
    setModal({ entity, trafficLight, assetData });
  };

  // ── Loading state ───────────────────────────────────────────────────────
  const isLoading = isFetchingOverview && allAssetsOverview.length === 0;

  if (assets.length === 0 || isLoading) {
    return (
      <div className="landing-loading">
        <div className="loading-spinner" />
        <p>Loading entity status overview across all assets…</p>
      </div>
    );
  }

  return (
    <div className="landing-page">

      {/* ── Page Header ──────────────────────────────────────────────────── */}
      <div className="landing-header">
        <div className="landing-header__left">
          <h2 className="landing-header__title">Production &amp; Projects Overview</h2>
          <p className="landing-header__subtitle">
            Real-time traffic light status across {assets.length} asset{assets.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="landing-header__actions">
          <button
            className="btn-refresh"
            onClick={fetchAllAssetsOverview}
            disabled={isFetchingOverview}
            title="Refresh all assets"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              className={isFetchingOverview ? 'spin' : ''}
            >
              <path d="M4 12C4 7.582 7.582 4 12 4C14.758 4 17.197 5.37 18.7 7.5L21 6V12H15L17.4 9.6C16.3 8.014 14.268 7 12 7C8.686 7 6 9.686 6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M20 12C20 16.418 16.418 20 12 20C9.242 20 6.803 18.63 5.3 16.5L3 18V12H9L6.6 14.4C7.7 15.986 9.732 17 12 17C15.314 17 18 14.314 18 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {isFetchingOverview ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn-dashboard" onClick={() => navigate('/dashboard')}>
            📊 Full Dashboard
          </button>
        </div>
      </div>

      {/* ── Notification due banner ───────────────────────────────────── */}
      {showNotifBanner && (
        <div className="notif-due-banner">
          <span className="notif-due-banner__icon">✉</span>
          <span>Scheduled notifications are due today.</span>
          <button className="notif-due-banner__link" onClick={() => navigate('/notifications')}>
            Go to Notifications →
          </button>
          <button className="notif-due-banner__close" onClick={() => setShowNotifBanner(false)}>✕</button>
        </div>
      )}

      {/* ── Summary Bar ──────────────────────────────────────────────────── */}
      <div className="summary-bar">
        <button
          className={`summary-pill summary-pill--amber ${colorFilter === 'amber' ? 'active' : ''}`}
          onClick={() => setColorFilter(colorFilter === 'amber' ? 'All' : 'amber')}
        >
          <span className="summary-dot summary-dot--amber" />
          <span className="summary-count">{summary.amber}</span>
          <span className="summary-label">At Risk</span>
        </button>

        <button
          className={`summary-pill summary-pill--red ${colorFilter === 'red' ? 'active' : ''}`}
          onClick={() => setColorFilter(colorFilter === 'red' ? 'All' : 'red')}
        >
          <span className="summary-dot summary-dot--red" />
          <span className="summary-count">{summary.red}</span>
          <span className="summary-label">Critical</span>
        </button>

        {summary.grey > 0 && (
          <button
            className={`summary-pill summary-pill--grey ${colorFilter === 'grey' ? 'active' : ''}`}
            onClick={() => setColorFilter(colorFilter === 'grey' ? 'All' : 'grey')}
          >
            <span className="summary-dot summary-dot--grey" />
            <span className="summary-count">{summary.grey}</span>
            <span className="summary-label">No Data</span>
          </button>
        )}

        <div className="summary-divider" />

        <span className="summary-total">
          {totalEntities} entities · {assets.length} asset{assets.length !== 1 ? 's' : ''}
        </span>

        {colorFilter !== 'All' && (
          <button className="filter-clear" onClick={() => setColorFilter('All')}>
            ✕ Clear filter
          </button>
        )}
      </div>

      {/* ── Asset Sections ────────────────────────────────────────────────── */}
      {allAssetsOverview.length === 0 ? (
        <div className="landing-empty">
          <p>No data available. Try refreshing.</p>
        </div>
      ) : (
        <div className="asset-sections">
          {allAssetsOverview.map((assetData) => (
            <AssetSection
              key={assetData.asset.Id}
              assetData={assetData}
              colorFilter={colorFilter}
              onCardClick={handleCardClick}
            />
          ))}
        </div>
      )}

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      {modal && (
        <FailingDepsModal
          entity={modal.entity}
          trafficLight={modal.trafficLight}
          latestSignals={modal.assetData.latestSignals}
          entities={modal.assetData.entities}
          assetName={modal.assetData.asset.Title}
          onClose={() => setModal(null)}
        />
      )}

      {/* Inline refresh indicator (when data already exists) */}
      {isFetchingOverview && allAssetsOverview.length > 0 && (
        <div className="landing-refresh-bar">
          <div className="loading-spinner loading-spinner--sm" />
          Refreshing all assets…
        </div>
      )}
    </div>
  );
};

export default LandingPage;
